import { contactLine } from "@/components/shared/StackedCell";
import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import {
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  Clock,
  Edit3,
  Download,
  Upload,
  FileDown,
  Filter,
  Users,
} from "lucide-react";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import { ImportDialog } from "@/components/shared/ImportDialog";
import {
  downloadExcelTemplate as downloadTemplate,
  type ExcelColumnDef,
} from "@/lib/excel.utils";
import { useExcelExport } from "@/hooks/useExcelExport";
import {
  InlineFilterRow,
  CHECKBOX_COL_NULL,
} from "@/components/shared/InlineFilterRow";
import type { FilterField } from "@/components/shared/FilterBar";
import { useRowSelection } from "@/hooks/useRowSelection";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import {
  BulkActionBar,
  type BulkAction,
} from "@/components/shared/BulkActionBar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ComboboxFK } from "@/components/shared/ComboboxFK";
import { trpcClient } from "@/lib/trpc";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { cn, viIncludes } from "@/lib/utils";
import { ListPageShell, useServerListFilter } from "@/components/shared/ListPageShell";
import { PageFilterTags } from "@/components/shared/PageFilterTags";
import { SortableHeader } from "@/components/shared/SortableHeader";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { useConfirm } from "@/providers/ConfirmProvider";
import { emsTabClass } from "@/components/ui/ems-tabs";

// ── Constants ──────────────────────────────────────────────────────────────────
const ITEMS_PER_PAGE = 50;

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "code", label: "Mã KH" },
  { key: "name", label: "Tên khách hàng" },
  { key: "shortName", label: "Tên viết tắt" },
  { key: "contactPerson", label: "Người liên hệ" },
  { key: "phone", label: "SĐT" },
  { key: "email", label: "Email" },
  { key: "currentDebt", label: "Nợ hiện tại", type: "number" },
  { key: "creditLimit", label: "Hạn mức nợ", type: "number" },
  { key: "address", label: "Địa chỉ" },
  { key: "group", label: "Nhóm" },
];

const DEMO_CUSTOMER_ROW: Record<string, string | number> = {
  code: "KH_VINAMILK",
  name: "Công ty Cổ phần Sữa Việt Nam",
  shortName: "Vinamilk",
  contactPerson: "Nguyễn Văn A",
  phone: "02854155555",
  email: "contact@vinamilk.com.vn",
  address: "Số 10 Tân Trào, P. Tân Phú, Quận 7, TP.HCM",
  group: "VIP",
};

const FILTER_FIELDS: FilterField[] = [
  { key: "code", label: "Mã KH", type: "text", placeholder: "Mã KH..." },
  { key: "name", label: "Tên KH", type: "text", placeholder: "Tên KH..." },
  { key: "salesName", label: "NV Sale", type: "text", placeholder: "NV Sale..." },
  {
    key: "rank6m",
    label: "Hạng KH",
    type: "select",
    options: [
      { value: "ALL", label: "Tất cả hạng" },
      { value: "VIP", label: "VIP" },
      { value: "TIER_A", label: "Hạng A" },
      { value: "TIER_B", label: "Hạng B" },
      { value: "TIER_C", label: "Hạng C" },
    ],
  },
  {
    key: "debtStatus",
    label: "Công nợ",
    type: "select",
    options: [
      { value: "ALL", label: "Tất cả công nợ" },
      { value: "OVER_LIMIT", label: "Vượt hạn mức" },
      { value: "APPROACHING_LIMIT", label: "Cận hạn mức" },
      { value: "SAFE", label: "Trong hạn mức" },
      { value: "NO_LIMIT", label: "Chưa đặt HM" },
    ],
  },
];

// ── Helpers ────────────────────────────────────────────────────────────────────
const formatMoney = (value: number | string | null | undefined): string => {
  const num = Number(value) || 0;
  if (num === 0) return "—";
  if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(1)}B`;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(0)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(0)}K`;
  return num.toLocaleString("vi-VN");
};

const formatShortDate = (d: string | Date | null | undefined): string => {
  if (!d) return "";
  const date = new Date(d);
  return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
};

const daysSince = (d: string | Date | null | undefined): number => {
  if (!d) return Infinity;
  return Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
};

export type CommercialRank = "VIP" | "TIER_A" | "TIER_B" | "TIER_C";
export type DebtStatus = "OVER_LIMIT" | "APPROACHING_LIMIT" | "SAFE" | "NO_LIMIT";

export const RANK_CONFIG: Record<
  CommercialRank,
  { label: string; badgeClass: string; desc: string }
> = {
  VIP: {
    label: "VIP",
    badgeClass: "bg-amber-500 text-white border-amber-600 font-bold shadow-xs",
    desc: "Doanh thu 6T ≥ 500M hoặc ≥ 20 đơn",
  },
  TIER_A: {
    label: "Hạng A",
    badgeClass: "bg-emerald-600 text-white border-emerald-700 font-semibold",
    desc: "Doanh thu 6T 200M - 500M hoặc 10 - 20 đơn",
  },
  TIER_B: {
    label: "Hạng B",
    badgeClass: "bg-sky-600 text-white border-sky-700 font-semibold",
    desc: "Doanh thu 6T 50M - 200M hoặc 3 - 10 đơn",
  },
  TIER_C: {
    label: "Hạng C",
    badgeClass: "bg-slate-100 text-slate-700 border-slate-300 font-medium",
    desc: "Doanh thu 6T < 50M và < 3 đơn",
  },
};

const computeRank6m = (revenue6m: number, orderCount6m: number): CommercialRank => {
  if (revenue6m >= 500_000_000 || orderCount6m >= 20) return "VIP";
  if (revenue6m >= 200_000_000 || orderCount6m >= 10) return "TIER_A";
  if (revenue6m >= 50_000_000 || orderCount6m >= 3) return "TIER_B";
  return "TIER_C";
};

const computeDebtStatus = (
  currentDebt: number,
  creditLimit: number | null | undefined,
): DebtStatus => {
  if (!creditLimit || creditLimit <= 0) return "NO_LIMIT";
  if (currentDebt > creditLimit) return "OVER_LIMIT";
  if (currentDebt >= creditLimit * 0.8) return "APPROACHING_LIMIT";
  return "SAFE";
};

const getSalesUserName = (c: any): string => {
  // Prefer salesUsers join table (many-to-many)
  if (c.salesUsers?.length > 0) {
    return c.salesUsers
      .map((su: any) => su.user?.fullName)
      .filter(Boolean)
      .join(", ");
  }
  // Fallback to legacy salesUser (1-1)
  return c.salesUser?.fullName || "";
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function CustomerList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showImport, setShowImport] = useState(false);
  const confirm = useConfirm();
  const [rankFilter, setRankFilter] = useState<CommercialRank | null>(null);
  const [debtFilter, setDebtFilter] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [showDeleted, setShowDeleted] = useState(false);
  const { hasPermission } = useRoleAccess();

  // ── Server-side Data ──
  const {
    items: rawItems,
    searchQuery,
    paginatedItems,
    isLoading,
    filtered,
    totalItems,
    statusCounts,
    meta,
    onStatusChange,
    onFilterChange,
    currentPage,
    pageSize,
    onPageChange,
    onPageSizeChange,
    showFilter,
    onToggleFilter,
    onClearAll,
    filterValues,
    serverParams,
    sortField,
    sortOrder,
    handleSort,
  } = useServerListFilter<any>({
    queryKey: "customers",
    queryFn: (params) => trpcClient.customers.list.query(params) as any,
    defaultLimit: 50,
    extraParams: {
      rank6m: rankFilter || undefined,
      debtStatus: debtFilter || undefined,
      isDeleted: showDeleted || undefined,
    },
  });

  // ── Enriched data (client-side only for display) ──
  const enrichedItems = useMemo(() => {
    return (paginatedItems || []).map((c: any) => {
      const rev6m = Number(c.revenue6m ?? c.orderValue ?? 0);
      const cnt6m = Number(c.orderCount6m ?? c._count?.orders ?? 0);
      const currentDebt = Number(c.currentDebt || 0);
      const creditLimit = c.creditLimit ? Number(c.creditLimit) : null;
      const rank6m: CommercialRank = (c.rank6m as CommercialRank) || computeRank6m(rev6m, cnt6m);
      const debtStatus: DebtStatus = (c.debtStatus as DebtStatus) || computeDebtStatus(currentDebt, creditLimit);
      const isOver = creditLimit !== null && creditLimit > 0 && currentDebt > creditLimit;
      const overAmount = isOver ? currentDebt - creditLimit : 0;
      const ratio = creditLimit && creditLimit > 0 ? Math.round((currentDebt / creditLimit) * 100) : 0;

      return {
        ...c,
        _salesName: getSalesUserName(c),
        _revenue6m: rev6m,
        _orderCount6m: cnt6m,
        _rank6m: rank6m,
        _currentDebt: currentDebt,
        _creditLimit: creditLimit,
        _debtStatus: debtStatus,
        _isOverLimit: isOver,
        _overAmount: overAmount,
        _debtRatio: ratio,
        _lastOrder: c.orders?.[0] || null,
      };
    });
  }, [paginatedItems]);

  // Use enriched items directly for display
  const displayItems = enrichedItems;

  const rowIds = useMemo(
    () => displayItems.map((c: any) => c.id),
    [displayItems],
  );
  const rowSelection = useRowSelection(rowIds);
  const { selectedCount, clearSelection, selectedIds } = rowSelection;

  // ── Actions ──
  const { handleExport, isExporting } = useExcelExport({
    filename: "danh_sach_khach_hang",
    sheetName: "Khách Hàng",
    columns: EXPORT_COLUMNS,
    queryFn: (params) => trpcClient.customers.list.query(params),
    currentFilters: {
      ...serverParams,
      status: statusFilter !== "all" && statusFilter ? statusFilter : undefined,
    },
    selectedIds,
  });

  const handleImport = async (rows: Record<string, unknown>[]) => {
    let success = 0;
    const errors: string[] = [];
    for (const row of rows) {
      try {
        await trpcClient.customers.create.mutate(row as any);
        success++;
      } catch (err: any) {
        errors.push(`${row.code}: ${err?.message || "Lỗi"}`);
      }
    }
    if (success > 0) {
      await queryClient.invalidateQueries({ queryKey: ["customers"] });
    }
    if (errors.length > 0)
      toast.error(
        `${errors.length} dòng lỗi: ${errors.slice(0, 3).join("; ")}`,
      );
  };

  const handleDelete = async (id: string) => {
    try {
      await trpcClient.customers.delete.mutate({ id });

      await queryClient.invalidateQueries({ queryKey: ["customers"] });
      await queryClient.invalidateQueries({ queryKey: ["customers-lookup"] });
      toast.success("Đã xóa bản ghi thành công.");
      setDeleteId(null);
    } catch (err: any) {
      toast.error(
        err?.message || "Xoá khách hàng thất bại. Kiểm tra ràng buộc dữ liệu.",
      );
    }
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) =>
      trpcClient.customers.delete.mutate({ id }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["customers"] });
      await queryClient.invalidateQueries({ queryKey: ["customers-lookup"] });
    },
    onError: async (err: any) => {
      toast.error(err?.message || "Xoá khách hàng thất bại.");
    },
  });

  const handleRestore = async (id: string) => {
    try {
      await trpcClient.customers.restore.mutate({ id });
      await queryClient.invalidateQueries({ queryKey: ["customers"] });
      await queryClient.invalidateQueries({ queryKey: ["customers-lookup"] });
      toast.success("Đã phục hồi bản ghi thành công.");
    } catch (err: any) {
      toast.error(
        err?.message || "Phục hồi khách hàng thất bại.",
      );
    }
  };

  const getBulkActions = (): BulkAction[] => {
    if (!selectedCount) return [];
    const actions: BulkAction[] = [];

    if (hasPermission("customers", "delete")) {
      actions.push({
        icon: <Trash2 className="h-4 w-4" />,
        label: `Xoá (${selectedCount})`,
        onClick: () => {
          executeBulkAction({
            confirmFn: confirm,
            selectedIds: Array.from(selectedIds),
            actionFn: async (id) => trpcClient.customers.delete.mutate({ id }),
            actionName: "xóa",
            itemType: "khách hàng",
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({ queryKey: ["customers"] });
              queryClient.invalidateQueries({ queryKey: ["customers-lookup"] });
            },
            requireConfirm: true,
            confirmMessage: `Bạn có chắc muốn xóa ${selectedCount} khách hàng đã chọn?`,
          });
        },
        variant: "destructive",
      });
    }
    return actions;
  };

  const rankFilterTags = useMemo(
    () => [
      {
        key: "VIP",
        label: "VIP",
        color: "bg-amber-500 text-white border-amber-600",
      },
      {
        key: "TIER_A",
        label: "Hạng A",
        color: "bg-emerald-600 text-white border-emerald-600",
      },
      {
        key: "TIER_B",
        label: "Hạng B",
        color: "bg-sky-600 text-white border-sky-600",
      },
      {
        key: "TIER_C",
        label: "Hạng C",
        color: "bg-slate-500 text-white border-slate-500",
      },
    ],
    [],
  );

  const debtFilterTags = useMemo(
    () => [
      {
        key: "OVER_LIMIT",
        label: "Vượt hạn mức",
        dotColor: "bg-rose-500",
        color: "bg-rose-50 text-rose-700 border-rose-200",
      },
      {
        key: "APPROACHING_LIMIT",
        label: "Cận hạn mức",
        dotColor: "bg-amber-500",
        color: "bg-amber-50 text-amber-700 border-amber-200",
      },
      {
        key: "SAFE",
        label: "Trong hạn mức",
        dotColor: "bg-emerald-500",
        color: "bg-emerald-50 text-emerald-700 border-emerald-200",
      },
      {
        key: "HAS_DEBT",
        label: "Có nợ",
        dotColor: "bg-blue-500",
        color: "bg-blue-50 text-blue-700 border-blue-200",
      },
      {
        key: "NO_LIMIT",
        label: "Chưa đặt HM",
        dotColor: "bg-slate-400",
        color: "bg-slate-50 text-slate-700 border-slate-200",
      },
    ],
    [],
  );

  return (
    <>
      <ListPageShell
        unitLabel="khách hàng"
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        showFilter={showFilter}
        onToggleFilter={onToggleFilter}
        onClearAll={onClearAll}
        filterFields={FILTER_FIELDS}
        filterValues={filterValues}
        isLoading={isLoading}
        tabs={
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setShowDeleted(false)}
              className={emsTabClass(!showDeleted, { size: "sm" })}
            >
              <Users className="w-3.5 h-3.5 text-indigo-600" />
              <span>Danh Sách Khách Hàng</span>
              <span
                className={cn(
                  "px-1.5 py-0.2 rounded-full text-[10px] font-bold tabular-nums",
                  !showDeleted
                    ? "bg-primary/10 text-primary"
                    : "bg-slate-200 text-slate-600"
                )}
              >
                {totalItems}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setShowDeleted(true)}
              className={emsTabClass(showDeleted, { size: "sm", tone: "rose" })}
            >
              <Trash2 className="w-3.5 h-3.5 text-rose-500" />
              <span>Thùng rác</span>
            </button>
          </div>
        }
        headerFilters={
          <div className="flex items-center gap-3 flex-wrap flex-1">
            {/* Status Filter (Tags) */}
            <div className="overflow-x-auto min-w-0 hide-scrollbar flex items-center">
              <PageFilterTags
                dropdownLabel="Trạng thái"
                items={[
                  {
                    key: "ACTIVE",
                    label: "Hoạt động",
                    dotColor: "bg-emerald-500",
                    color: "bg-emerald-50 text-emerald-700 border-emerald-200",
                  },
                  {
                    key: "INACTIVE",
                    label: "Ngưng",
                    dotColor: "bg-rose-500",
                    color: "bg-rose-50 text-rose-700 border-rose-200",
                  },
                ]}
                selectedKeys={
                  statusFilter === "all" || !statusFilter ? [] : [statusFilter]
                }
                onSelect={(key) => {
                  const val = !key ? "all" : key;
                  setStatusFilter(val);
                  onStatusChange(val === "all" ? null : val);
                }}
                hideEmpty={false}
              />
            </div>

            <div className="h-5 w-px bg-slate-200 hidden sm:block" />

            {/* Rank 6T Filter (Tags) */}
            <div className="flex-1 overflow-x-auto min-w-0 hide-scrollbar flex items-center">
              <PageFilterTags
                dropdownLabel="Hạng KH (6T)"
                items={rankFilterTags}
                selectedKeys={rankFilter ? [rankFilter] : []}
                onSelect={(key) => {
                  setRankFilter(
                    key === rankFilter || !key ? null : (key as CommercialRank),
                  );
                }}
                hideEmpty={false}
              />
            </div>

            <div className="h-5 w-px bg-slate-200 hidden sm:block" />

            {/* Debt Filter (Tags) */}
            <div className="overflow-x-auto min-w-0 hide-scrollbar flex items-center">
              <PageFilterTags
                dropdownLabel="Công nợ"
                items={debtFilterTags}
                selectedKeys={debtFilter ? [debtFilter] : []}
                onSelect={(key) => {
                  setDebtFilter(
                    key === debtFilter || !key ? null : key,
                  );
                }}
                hideEmpty={false}
              />
            </div>
          </div>
        }
        actions={
          <div className="flex items-center gap-1.5">
            <ExportImportButtons
              onExport={handleExport}
              exportLoading={isExporting}
              onDownloadTemplate={() => downloadTemplate(EXPORT_COLUMNS, "khach_hang", [DEMO_CUSTOMER_ROW])}
              onImportClick={hasPermission("customers", "create") ? () => setShowImport(true) : undefined}
            >
              {hasPermission("customers", "create") && (
                <Button
                  size="sm"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium h-7 text-xs px-2.5"
                  onClick={() => navigate({ to: "/customers/create" })}
                >
                  <Plus className="mr-1 h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">Thêm KH</span>
                </Button>
              )}
            </ExportImportButtons>
          </div>
        }
      >
          <table
            className={cn(
              "w-full text-xs caption-bottom apv-compact-table min-w-[1120px]",
              rowSelection.selectionMode && "apv-select-mode",
            )}
          >
            <thead className="sticky top-0 z-10 apv-table-header">
              <tr className="bg-slate-100/80 backdrop-blur-sm border-b border-slate-200">
                <DataTableSelectAllCheckbox rowSelection={rowSelection} />
                <SortableHeader
                  label="Mã KH"
                  field="code"
                  currentField={sortField}
                  currentOrder={sortOrder}
                  onSort={handleSort}
                  className="px-3 py-2.5 font-semibold text-slate-700 uppercase tracking-wider text-[10px] w-[95px] apv-col-code"
                />
                <SortableHeader
                  label="Tên khách hàng"
                  field="name"
                  currentField={sortField}
                  currentOrder={sortOrder}
                  onSort={handleSort}
                  className="px-3 py-2.5 font-semibold text-slate-700 uppercase tracking-wider text-[10px] min-w-[200px]"
                />
                <th
                  className="text-right px-3 py-2.5 font-semibold text-slate-700 uppercase tracking-wider text-[10px] w-[110px] whitespace-nowrap"
                  title="Doanh thu tích lũy trong 6 tháng gần nhất"
                >
                  Doanh thu (6T)
                </th>
                <th
                  className="text-center px-3 py-2.5 font-semibold text-slate-700 uppercase tracking-wider text-[10px] w-[100px] whitespace-nowrap"
                  title="Số lượng đơn và ngày đặt gần nhất trong 6 tháng"
                >
                  Đơn hàng (6T)
                </th>
                <th
                  className="text-center px-3 py-2.5 font-semibold text-slate-700 uppercase tracking-wider text-[10px] w-[95px] whitespace-nowrap"
                  title="Xếp hạng thương mại theo doanh thu và đơn hàng 6 tháng"
                >
                  Hạng KH (6T)
                </th>
                <th
                  className="text-left px-3 py-2.5 font-semibold text-slate-700 uppercase tracking-wider text-[10px] min-w-[190px] whitespace-nowrap"
                  title="Công nợ thực tế so với hạn mức tín dụng"
                >
                  Công nợ / Hạn mức
                </th>
                <SortableHeader
                  label="NV Sale"
                  field="salesUser.fullName"
                  currentField={sortField}
                  currentOrder={sortOrder}
                  onSort={handleSort}
                  className="px-3 py-2.5 font-semibold text-slate-700 uppercase tracking-wider text-[10px] w-[130px]"
                />
                <th className="text-right px-3 py-2.5 font-semibold text-slate-700 uppercase tracking-wider text-[10px] w-[70px] whitespace-nowrap apv-col-actions">
                  Thao tác
                </th>
              </tr>
              {showFilter && (
                <InlineFilterRow
                  columns={[
                    CHECKBOX_COL_NULL,
                    { key: "code" },
                    { key: "name" },
                    null,
                    null,
                    { key: "rank6m" },
                    { key: "debtStatus" },
                    { key: "salesName" },
                    null,
                  ]}
                  filterFields={FILTER_FIELDS}
                  filterValues={filterValues}
                  onFilterChange={onFilterChange}
                />
              )}
            </thead>
            <tbody className="bg-white">
              {isLoading ? (
                Array.from({ length: 12 }).map((_, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="px-2 py-3">
                      <Skeleton className="h-3.5 w-4" />
                    </td>
                    {Array.from({ length: 8 }).map((_, j) => (
                      <td key={j} className="px-3 py-3">
                        <Skeleton className="h-3.5 w-16" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : displayItems.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="text-center text-slate-600 py-16 text-sm"
                  >
                    {searchQuery || rankFilter || debtFilter
                      ? "Không tìm thấy khách hàng phù hợp"
                      : "Chưa có dữ liệu khách hàng"}
                  </td>
                </tr>
              ) : (
                displayItems.map((c: any) => {
                  const lastOrder = c._lastOrder;

                  return (
                    <DataTableRow
                      key={c.id}
                      id={c.id}
                      rowSelection={rowSelection}
                      onClick={() =>
                        navigate({
                          to: "/customers/$customerId",
                          params: { customerId: c.id },
                        })
                      }
                      className="border-b border-slate-100 hover:bg-blue-50/40 cursor-pointer transition-colors group"
                    >
                      <DataTableRowCheckbox
                        id={c.id}
                        rowSelection={rowSelection}
                      />
                      {/* Mã KH */}
                      <td className="px-3 py-2.5 apv-col-code">
                        <span className="font-semibold text-slate-900 whitespace-nowrap">
                          {c.code}
                        </span>
                      </td>

                      {/* Tên KH */}
                      <td className="px-3 py-2.5">
                        <div className="font-semibold text-slate-900 min-w-[200px] break-words whitespace-normal leading-tight text-[12px]">
                          {c.shortName || c.name}
                        </div>
                        {c.shortName && c.name !== c.shortName && (
                          <div className="text-[10px] text-slate-600 min-w-[200px] break-words whitespace-normal leading-tight mt-0.5">
                            {c.name}
                          </div>
                        )}
                        {/* Người liên hệ · SĐT ngay dưới tên (30/10/2026) */}
                        {contactLine(c.contactPerson, c.phone) && (
                          <div className="text-[11px] text-slate-500 mt-0.5 truncate" data-testid="customer-row-contact">
                            {contactLine(c.contactPerson, c.phone)}
                          </div>
                        )}
                      </td>

                      {/* Tổng Doanh Thu 6T */}
                      <td className="px-3 py-2.5 text-right">
                        {c._revenue6m > 0 ? (
                          <span
                            className="font-bold text-emerald-600 text-[12px] tabular-nums"
                            title={`${c._revenue6m.toLocaleString("vi-VN")} đ`}
                          >
                            {formatMoney(c._revenue6m)}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>

                      {/* ĐH Gần Nhất (6T) */}
                      <td className="px-3 py-2.5 text-center">
                        {c._orderCount6m > 0 ? (
                          <div className="flex flex-col items-center">
                            <span className="font-semibold text-blue-600 text-[11px]">
                              {c._orderCount6m} đơn
                            </span>
                            {lastOrder && (
                              <span className="text-[10px] text-slate-500">
                                {formatShortDate(lastOrder.orderDate)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-[11px]">Chưa có đơn</span>
                        )}
                      </td>

                      {/* Hạng KH (6T) */}
                      <td className="px-3 py-2.5 text-center">
                        <Badge
                          className={cn(
                            "text-[10px] px-2 py-0.5 border cursor-default shadow-2xs",
                            RANK_CONFIG[c._rank6m as CommercialRank]?.badgeClass ||
                              "bg-slate-100 text-slate-700 border-slate-300",
                          )}
                          title={RANK_CONFIG[c._rank6m as CommercialRank]?.desc}
                        >
                          {RANK_CONFIG[c._rank6m as CommercialRank]?.label || c._rank6m}
                        </Badge>
                      </td>

                      {/* Công Nợ / Hạn Mức */}
                      <td className="px-3 py-2.5">
                        <div className="flex flex-col gap-1">
                          {/* Dòng 1: Nợ hiện tại (Link sang Sổ công nợ) */}
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-slate-500 font-medium">Nợ HT:</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate({
                                  to: "/debts",
                                  search: { customerId: c.id } as any,
                                });
                              }}
                              className={cn(
                                "text-xs font-bold tabular-nums text-left hover:underline cursor-pointer transition-colors",
                                c._isOverLimit
                                  ? "text-rose-600 font-extrabold hover:text-rose-700"
                                  : c._currentDebt > 0
                                  ? "text-slate-900 hover:text-indigo-600"
                                  : "text-slate-400 font-normal hover:text-indigo-600",
                              )}
                              title="Bấm để xem Sổ chi tiết công nợ khách hàng"
                            >
                              {c._currentDebt > 0
                                ? `${c._currentDebt.toLocaleString("vi-VN")} đ`
                                : "0 đ"}
                            </button>
                          </div>

                          {/* Dòng 2: Hạn mức + Badge đánh giá */}
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[10px] text-slate-500">
                              HM: {c._creditLimit ? `${formatMoney(c._creditLimit)} đ` : "Chưa đặt"}
                            </span>
                            {c._debtStatus === "OVER_LIMIT" && (
                              <Badge className="text-[9px] px-1.5 py-0 bg-rose-50 text-rose-700 border border-rose-300 font-bold inline-flex items-center gap-0.5">
                                <AlertTriangle className="h-2.5 w-2.5 text-rose-600 shrink-0" />
                                Vượt HM (+{formatMoney(c._overAmount)})
                              </Badge>
                            )}
                            {c._debtStatus === "APPROACHING_LIMIT" && (
                              <Badge className="text-[9px] px-1.5 py-0 bg-amber-50 text-amber-700 border border-amber-300 font-bold inline-flex items-center gap-0.5">
                                <AlertCircle className="h-2.5 w-2.5 text-amber-600 shrink-0" />
                                Cận HM ({c._debtRatio}%)
                              </Badge>
                            )}
                            {c._debtStatus === "SAFE" && (
                              <Badge className="text-[9px] px-1.5 py-0 bg-emerald-50 text-emerald-700 border border-emerald-300 font-medium inline-flex items-center gap-0.5">
                                <CheckCircle2 className="h-2.5 w-2.5 text-emerald-600 shrink-0" />
                                Trong HM
                              </Badge>
                            )}
                            {c._debtStatus === "NO_LIMIT" && (
                              <Badge className="text-[9px] px-1.5 py-0 bg-slate-50 text-slate-500 border border-slate-200 font-normal">
                                Chưa đặt HM
                              </Badge>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* NV Sale */}
                      <td className="px-3 py-2.5">
                        {c._salesName ? (
                          <span className="text-slate-700 text-[11px] truncate block max-w-[140px]">
                            {c._salesName}
                          </span>
                        ) : (
                          <span className="text-slate-300 text-[11px]">—</span>
                        )}
                      </td>

                      {/* Thao Tác */}
                      <td
                        className="px-3 py-2.5 text-right apv-col-actions"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-slate-500 hover:text-indigo-600 hover:bg-slate-100 relative z-10"
                              title="Thao tác"
                            >
                              <MoreHorizontal size={14} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44 font-medium">
                            {!c.isDeleted ? (
                              <>
                                {hasPermission("customers", "update") && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      navigate({
                                        to: "/customers/$customerId/edit",
                                        params: { customerId: c.id },
                                      })
                                    }
                                  >
                                    <Pencil size={13} className="mr-2" /> Sửa thông tin
                                  </DropdownMenuItem>
                                )}
                                {hasPermission("customers", "delete") && (
                                  <DropdownMenuItem
                                    className="text-rose-600 focus:text-rose-600"
                                    onClick={() => setDeleteId(c.id)}
                                  >
                                    <Trash2 size={13} className="mr-2" /> Xoá
                                  </DropdownMenuItem>
                                )}
                              </>
                            ) : (
                              <>
                                {hasPermission("customers", "delete") && (
                                  <DropdownMenuItem
                                    className="text-emerald-600 focus:bg-emerald-50 focus:text-emerald-700"
                                    onClick={() => handleRestore(c.id)}
                                  >
                                    <CheckCircle2 size={13} className="mr-2" /> Phục hồi
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </DataTableRow>
                  );
                })
              )}
            </tbody>
          </table>
      </ListPageShell>

      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={getBulkActions()}
      />

      {/* Delete dialog */}
      <AlertDialog
        open={!!deleteId}
        onOpenChange={(open) => !open && setDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xác nhận xoá</AlertDialogTitle>
            <AlertDialogDescription>
              Bạn có chắc muốn xoá khách hàng này? Hành động không thể hoàn tác.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Huỷ</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && handleDelete(deleteId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Xoá
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ImportDialog
        open={showImport}
        onOpenChange={setShowImport}
        title="Import Khách Hàng"
        columns={EXPORT_COLUMNS}
        onConfirmImport={handleImport}
        onDownloadTemplate={() => downloadTemplate(EXPORT_COLUMNS, "khach_hang", [DEMO_CUSTOMER_ROW])}
      />
    </>
  );
}
